const HEX_COLOR_PATTERN = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const RGB_COLOR_PATTERN = /^rgba?\(([^)]*)\)$/i;

const HSL_COLOR_PATTERN = /^hsla?\(([^)]*)\)$/i;

const RGB_CHANNEL_SEPARATOR = /[\s,/]+/;

const NAMED_COLOR_CHANNELS: Record<string, Array<number>> = {
    black: [
        0,
        0,
        0
    ],
    white: [
        255,
        255,
        255
    ]
};

const HSL_CHANNEL_OFFSETS = [
    0,
    8,
    4
];

const ALPHA_PRECISION = 1000;

const HUE_SECTORS = 12;

const HUE_SECTOR = 30;

const MAX_CHANNEL = 255;

const MAX_PERCENT = 100;

const clampChannel = (channel: number) => {
    return Math.min(Math.max(Math.round(channel), 0), MAX_CHANNEL);
};

const parseRGBChannel = (channel: string) => {
    if(channel.endsWith("%")) {
        return clampChannel(parseFloat(channel) / MAX_PERCENT * MAX_CHANNEL);
    }

    return clampChannel(parseFloat(channel));
};

const parseHexChannels = (hex: string): Array<number> => {
    const pairs = hex.length < 6 ? hex.split("").map((digit) => `${digit}${digit}`) : hex.match(/.{2}/g) ?? [];

    return pairs.slice(0, 3).map((pair) => parseInt(pair, 16));
};

const parseRGBChannels = (body: string): Array<number> => {
    return body.split(RGB_CHANNEL_SEPARATOR).filter((channel) => channel.length > 0).slice(0, 3).map(parseRGBChannel);
};

const parseHSLChannels = (body: string): Array<number> => {
    const [
        hue = "",
        saturation = "",
        lightness = ""
    ] = body.split(RGB_CHANNEL_SEPARATOR).filter((channel) => channel.length > 0);

    const currentLightness = parseFloat(lightness) / MAX_PERCENT;

    const chroma = parseFloat(saturation) / MAX_PERCENT * Math.min(currentLightness, 1 - currentLightness);

    return HSL_CHANNEL_OFFSETS.map((offset) => {
        const sector = ((offset + parseFloat(hue) / HUE_SECTOR) % HUE_SECTORS + HUE_SECTORS) % HUE_SECTORS;

        return clampChannel((currentLightness - chroma * Math.max(-1, Math.min(sector - 3, 9 - sector, 1))) * MAX_CHANNEL);
    });
};

const getColorChannels = (color: string): Array<number> => {
    const namedColor = NAMED_COLOR_CHANNELS[color.toLowerCase()];

    if(namedColor) {
        return namedColor;
    }

    const hexColor = HEX_COLOR_PATTERN.exec(color);

    if(hexColor && hexColor[1]) {
        return parseHexChannels(hexColor[1]);
    }

    const rgbColor = RGB_COLOR_PATTERN.exec(color);

    if(rgbColor && rgbColor[1]) {
        return parseRGBChannels(rgbColor[1]);
    }

    const hslColor = HSL_COLOR_PATTERN.exec(color);

    if(hslColor && hslColor[1]) {
        return parseHSLChannels(hslColor[1]);
    }

    return [];
};

const getColorAlpha = (color: string): number => {
    const rgbColor = RGB_COLOR_PATTERN.exec(color);

    if(!rgbColor || !rgbColor[1]) {
        return 1;
    }

    const alpha = rgbColor[1].split(RGB_CHANNEL_SEPARATOR).filter((channel) => channel.length > 0)[3];

    if(alpha === undefined) {
        return 1;
    }

    const currentAlpha = alpha.endsWith("%") ? parseFloat(alpha) / MAX_PERCENT : parseFloat(alpha);

    return Number.isNaN(currentAlpha) ? 1 : Math.min(Math.max(currentAlpha, 0), 1);
};

export const withAlpha = ({
    color,
    alpha
}: {
    color: string;
    alpha: number;
}): string => {
    const channels = getColorChannels(color.trim());

    if(channels.length < 3 || channels.some((channel) => Number.isNaN(channel)) || Number.isNaN(alpha)) {
        return color;
    }

    const currentAlpha = Math.round(Math.min(Math.max(alpha, 0), 1) * ALPHA_PRECISION) / ALPHA_PRECISION;

    return `rgba(${channels.join(",")},${currentAlpha})`;
};

export const getSurfaceRelativeColor = ({
    surface,
    ground,
    color
}: {
    surface: string;
    ground: string;
    color: string;
}): string => {
    const channels = getColorChannels(color.trim());

    const surfaceChannels = getColorChannels(surface.trim());

    const groundChannels = getColorChannels(ground.trim());

    if([
        channels,
        surfaceChannels,
        groundChannels
    ].some((list) => list.length < 3 || list.some((channel) => Number.isNaN(channel)))) {
        return color;
    }

    return `#${channels.map((channel, index) => clampChannel(channel + (surfaceChannels[index] ?? 0) - (groundChannels[index] ?? 0)).toString(16).padStart(2, "0")).join("")}`;
};

export const getOpaqueColor = ({
    surface,
    color
}: {
    surface: string;
    color: string;
}): string => {
    const channels = getColorChannels(color.trim());

    const surfaceChannels = getColorChannels(surface.trim());

    if([
        channels,
        surfaceChannels
    ].some((list) => list.length < 3 || list.some((channel) => Number.isNaN(channel)))) {
        return color;
    }

    const alpha = getColorAlpha(color.trim());

    return `#${channels.map((channel, index) => clampChannel(channel * alpha + (surfaceChannels[index] ?? 0) * (1 - alpha)).toString(16).padStart(2, "0")).join("")}`;
};
